<template>
  <section>
    <div class="registration-form__inner-container">
      <EmailVerification :isLandingPage="isLandingPage" @verification:successful="handleVerificationEvent" v-if="verificationEmail" />
      <form autocomplete="off" v-else @submit.prevent="register">
        <div 
          :class="{ 
            'logo-container': true, 
            'justify-content-start': getCompany.logo_placement === 'left', 
            'justify-content-center': getCompany.logo_placement === 'center', 
            'registration-form__company-logo mb-3': true,

          }">
          <img 
            :src="getCompany.logo_link"
            alt="SeamlessHiring"
          />
        </div>
        <div>
          <h1 class="fs-20 fw-500 text-black-1 registration-form__primary-heading text-center">Create Your Candidate Profile</h1>
          <p class="fs-15 text-muted mt-2 mb-3 text-center">Save your profile, track applications, and apply faster in the future.</p>
        </div>
        <div class="form-group mb-3">
          <SfeLabel required class="text-black-1 fs-14 fw-400">Email</SfeLabel>
          <SfeInput v-model="form.email" type="email" placeholder="example@gmail.com" :disabled="!!prefillEmail" />
          <p v-if="prefillEmail" class="fs-13 mt-1 mb-0" style="color: #e07c24;">Using the email from your application.</p>
          <ShrInputError :error="errors.email" />
        </div>
        <div class="row">
          <div class="col-md-6">
            <div class="form-group mb-3">
              <SfeLabel required class="text-black-1 fs-14 fw-400">First name</SfeLabel>
              <SfeInput v-model="form.first_name" type="text" placeholder="John" />
              <ShrInputError :error="errors.first_name" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-3">
              <SfeLabel required class="text-black-1 fs-14 fw-400">Surname</SfeLabel>
              <SfeInput v-model="form.last_name" type="text" placeholder="Doe" />
              <ShrInputError :error="errors.last_name" />
            </div>
          </div>
        </div>
        <div class="form-group mb-3">
          <SfeLabel required class="text-black-1 fs-14 fw-400 d-inline-flex">Password</SfeLabel> <small class="text-muted font-italic">(Min 8 chars, uppercase, lowercase and symbol)</small>
            <PasswordInput v-model="form.password" />
          <ShrInputError :error="errors.password" />
        </div>
        <div class="form-group mb-3">
          <SfeLabel required class="text-black-1 fs-14 fw-400">Confirm Password</SfeLabel>
          <PasswordInput v-model="form.password_confirmation" />
          <ShrInputError :error="errors.password_confirmation" />
        </div>
        <div class="form-group mb-3 pt-2">
          <SfeButton type="submit" :disabled="conditionsToDisableCreateAccountButton" class="btn fs-18 fw-400 text-center btn-primary-light-blue border-radius-8 w-100 py-2">
            <span class="d-flex align-items justify-content-center">
              <span>Create Candidate Profile</span>
              <SfeLoader size="sm" color="white" class="ml-1" v-if="submitting" />
            </span>
          </SfeButton>
        </div>
      </form>
      <div :class="[isLandingPage ? '' : 'd-none', 'w-75 ml-auto mr-auto']">
        <div class="text-center">
          <p class="text-light-grey-1 ml-auto mr-auto">Already have an account?<a href="#" @click="handleLoginLinkClick" class="text-primary ml-1 fw-500">Log in</a></p>
        </div>
      </div>
      <ClientPrivacyPolicy></ClientPrivacyPolicy>
      <p class="text-light-grey-1 text-center mt-2" v-if="$helpers.getObjectProperty(company, 'client.name', 'SeamlessHiring') == 'KPMG'">
        <small>
          The KPMG trademarks are the sole property of KPMG International Cooperative
        </small>
      </p>
    </div>
  </section>
</template>

<script>
import CandidateModel from "../../models/CandidateModel"
import ClientPrivacyPolicy from "../../components/auth/ClientPrivacyPolicy"
import EmailVerification from "./EmailVerification"
import PasswordInput from "../../components/auth/PasswordInput"

export default {
  mixins: [ CandidateModel ],
  components: { EmailVerification, ClientPrivacyPolicy, PasswordInput},
  data(){
    return {
      form: {
        first_name: '',
        last_name: '',
        password: '',
        password_confirmation: '',
        email: '',
      },
      passwordFieldType: false,
      confirmPassword: false,
      verificationEmail: this.$store.state.verificationEmail,
      submitting: false,
      errors: {},
      getCompany: {},
    }
  },
  props: {
    isLandingPage: {
      type: Boolean,
      default: false,
    },
    company: {
      type: Object,
      default: () => ({})
    },
    prefillEmail: {
      type: String,
      default: ''
    }
  },
  computed: {
    conditionsToDisableCreateAccountButton() {
      return this.submitting || this.buttonIsDisabled
    },
    buttonIsDisabled() {
      return Object.values(this.form).some(parameter => !parameter)
    }
  },
  mounted() {
    const verificationEmail = sessionStorage.getItem('verificationEmail')
    if (verificationEmail) this.$store.commit('SET_SHOW_VERIFICATION', verificationEmail)
    if (this.prefillEmail) this.form.email = this.prefillEmail
  },

  methods: {
    register(){
      if (this.conditionsToDisableCreateAccountButton) {
        return
      }
      this.submitting = true
      this.errors = {};
      this.CandidateModel.register(this.form).then(r => {
        this.trackEvent('sign-up: account created successfully');
        this.$sfeToast.toast('success', 'Success', r.message)
        this.verificationEmail = this.form.email
        this.$store.commit('SET_SHOW_VERIFICATION', this.form.email)
      }).catch(e => {
        this.trackEvent('sign-up: account creation failed');
        let passwordMismatchIndex
        if (e.errors && e.errors.password && 
          e.errors.password.some((passwordErr, index) => {
            if (passwordErr === 'Password does not match') {
              passwordMismatchIndex = index
              return true
            } 
            return false
          })) 
        {
          // let password confirmation error show in confirmation_password field
          e.errors.password_confirmation = e.errors.password.slice(0, 1)
          e.errors.password.splice(passwordMismatchIndex, 1)
        }
        this.errors = e?.errors || {}
      }).finally(() => {
        this.submitting = false
      })
    },
    handleVerificationEvent(data){
      sessionStorage.removeItem('verificationEmail')
      this.proceed(data)
    },

    proceed(data){
      this.$store.commit('SET_LOGGED_IN_USER', data)
      this.$emit('registration:successful')
    },
    handleLoginLinkClick() {
      this.$store.commit('CLEAR_VERIFICATION_EMAIL')
      this.$emit('show:login-form')
    },
  },
  watch: {
    company: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.getCompany = newValue
      }
    }
  }
}
</script>


<style scoped lang="scss">
  @media screen and (min-width: 320px) {
    .registration-form {
      &__inner-container {
        background-color: #ffffff;
        padding: 20px 12px;
      }
      &__company-logo {
        display: block;
        width: 80px;
        margin-left: auto;
        margin-right: auto;
      }
    }
  }
  @media screen and (min-width: 760px) {

    .registration-form {
      &__inner-container {
        background-color: none;
        padding: 0;
      }
      &__company-logo {
        display: none
      }
    }
  }
</style>